import { ReactNode, useEffect, useRef } from "react";
import {
  NavLink,
  useLocation,
  useNavigate,
  useSearchParams,
} from "react-router-dom";
import { useActionExecutor } from "../../customeHooks/useActionExecutor";
import { baseApi } from "../../redux/api";
import { authSliceActions } from "../../redux/features/auth/auth.slice";
import { useAppDispatch, useAppSelector } from "../../redux/hooks";
import { LANDING_PAGE_URL, resumeUrlKey } from "../../utils/constants";
import { isClientAuthCookieSet, sendSupportEmail } from "../../utils/helpers";
import { Button } from "../buttons/button";
import { ScrollToTop } from "../scrollToTop";
import { HelpWidget } from "./components/app/helpWidget";

export const AuthLayout = ({ children }: { children: ReactNode }) => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const location = useLocation();

  const container = useRef<HTMLDivElement>(null);
  const helpWidgetRef = useRef<HTMLButtonElement>(null);

  const { isSignedIn } = useAppSelector((state) => state.auth);

  const { action } = useActionExecutor();

  const dispatch = useAppDispatch();

  useEffect(() => {
    const resume = searchParams.get("resume")?.trim();

    // Set resume url if any
    if (resume) {
      localStorage.setItem(resumeUrlKey, resume);
      setSearchParams({});
    }

    const isAuthSet = isClientAuthCookieSet();

    if (isAuthSet && isSignedIn && action === "accept-invite") {
      dispatch(authSliceActions.signUserOut());
      return;
    }

    if (isAuthSet && isSignedIn) {
      const resumeUrl = localStorage.getItem(resumeUrlKey);
      if (resumeUrl) {
        navigate(resumeUrl);
        localStorage.removeItem(resumeUrlKey);
      } else {
        navigate("/home");
      }
    } else {
      dispatch(baseApi.util.resetApiState());
    }
    // eslint-disable-next-line
  }, [location.pathname]);

  return (
    <>
      <ScrollToTop
        changeKey={location.pathname}
        rerunOnKeyChange
        scrollIntoView
      />

      <div className="flex h-screen max-h-[100dvh] flex-col bg-grey-50">
        <div className="flex items-center justify-between border-b border-grey-100 py-6 md:py-6">
          <div className="max-width">
            <NavLink to={LANDING_PAGE_URL} className={"block w-fit"}>
              <img src="/assets/logo_full.svg" className="bg-red" alt="logo" />
            </NavLink>
          </div>

          <Button
            type="button"
            onClick={() => {
              sendSupportEmail();
            }}
            text="Get Help"
            icon="circle-help"
            className="!gap-1.5 !p-0 !px-0 !py-0 md:!hidden"
            variation="grey-800"
            iconDimension={24}
          />
        </div>

        <div id="pageTopRefToScroll" className="h-0" />
        <div
          ref={container}
          className="max-width flex flex-1 justify-center overflow-y-scroll py-9 md:py-16"
        >
          <div className="h-fit w-full max-w-[440px] rounded-2xl bg-white px-5 py-16 md:p-8">
            {children}
          </div>
        </div>
      </div>

      <div className="!fixed bottom-4 right-4 z-[999] w-fit md:bottom-7 md:right-10">
        <HelpWidget helpWidgetRef={helpWidgetRef} />
      </div>
    </>
  );
};
